import { Component } from '@theme/component';
import {
  fetchConfig,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { fetchCartState, runCartMutation, runCartRequest } from '@theme/skin-elite-ebook-gift';
import { createCartLineIdentity, findCartLine, cancelPendingLineChanges } from './skin-elite-cart-line.js?v=fc89a00c8f482b52';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #quantityChanges = new Map();
  #pendingLines = new Map();
  #savedDisabled = new WeakMap();

  #rowIdentity(row) {
    if (!row) return undefined;
    try {
      return createCartLineIdentity({
        variantId: row.dataset.cartVariantId || row.dataset.key?.split(':')[0],
        properties: JSON.parse(row.dataset.cartLineProperties || '{}'),
        sellingPlanId: row.dataset.cartSellingPlanId || '',
      });
    } catch (_error) {
      return undefined;
    }
  }

  #cancelQuantityChange(key, identity) {
    cancelPendingLineChanges(this.#quantityChanges, key, identity);
  }

  #applyPendingState() {
    const pending = [...this.#pendingLines.values()];
    for (const row of this.querySelectorAll('tr[data-key]')) {
      const identity = this.#rowIdentity(row);
      const busy = pending.some((item) => item.key === row.dataset.key || (identity && item.identity === identity));
      if (busy) row.setAttribute('aria-busy', 'true');
      else if (row.dataset.bundleBusy !== 'true') row.removeAttribute('aria-busy');
      for (const control of row.querySelectorAll('button, input')) {
        if (busy) {
          if (!this.#savedDisabled.has(control)) this.#savedDisabled.set(control, control.disabled);
          control.disabled = true;
        } else if (this.#savedDisabled.has(control)) {
          control.disabled = this.#savedDisabled.get(control);
          this.#savedDisabled.delete(control);
        }
      }
    }
  }

  // Each row owns its debounce: a quick change on another product cannot cancel it.
  #onQuantityInput = (event) => {
    if (!(event instanceof QuantitySelectorUpdateEvent) || !(event.target instanceof Element) || !this.contains(event.target)) return;
    const row = event.target.closest('tr[data-key]');
    if (!row || this.#pendingLines.has(row.dataset.key)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;
    const key = row.dataset.key;
    const identity = this.#rowIdentity(row);
    this.#cancelQuantityChange(key, identity);
    const change = { line, quantity, action: quantity === 0 ? 'clear' : 'change', row, lineId: key, identity };
    const timer = setTimeout(() => {
      this.#quantityChanges.delete(key);
      this.updateQuantity(change);
    }, 300);
    this.#quantityChanges.set(key, { timer, change, identity });
  };
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? 'hydration' : 'full',
          });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityInput);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
  }

  /** Commit visible quantity edits before checkout guards take their final cart snapshot. */
  flushQuantityChanges() {
    const changes = [...this.#quantityChanges.values()];
    this.#quantityChanges.clear();
    for (const { timer, change } of changes) {
      clearTimeout(timer);
      this.updateQuantity(change);
    }
  }

  /** Remove the clicked line by identity, not a positional index that may have shifted. */
  onLineItemRemove(line, event) {
    const row = event?.target instanceof Element
      ? event.target.closest('tr[data-key]')
      : this.refs.cartItemRows[line - 1];
    if (!row) return;
    this.#cancelQuantityChange(row.dataset.key, this.#rowIdentity(row));
    // Keep the row until Shopify confirms removal. Failed requests remain recoverable.
    return this.updateQuantity({ line, quantity: 0, action: 'clear', row });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    const { line, quantity } = config;
    const lineItemRow = config.row || this.refs.cartItemRows[line - 1];
    const lineId = config.lineId || lineItemRow?.dataset.key;
    if (!lineId || this.#pendingLines.has(lineId)) return;
    const identity = config.identity || this.#rowIdentity(lineItemRow);
    this.#pendingLines.set(lineId, { key: lineId, identity });
    this.#applyPendingState();
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    deferredUpdatePromise.promise.catch(() => {});
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
        context: 'cart',
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      })
    );

    return runCartMutation(async () => {
      // Resolve inside the shared queue; earlier actions can change discount keys and row order.
      const currentCart = await fetchCartState();
      const currentLine = findCartLine(currentCart, lineId, identity);
      if (!currentLine) {
        if (quantity === 0 && !currentCart.items.some((item) => String(item.variant_id) === lineId.split(':')[0])) return currentCart;
        throw new Error('Your cart changed. Please refresh it before updating.');
      }
      const body = JSON.stringify({
        id: currentLine.key,
        quantity,
        sections: Array.from(sectionsToUpdate).slice(0, 5).join(','),
        sections_url: window.location.pathname,
      });
      return runCartRequest(async (signal) => {
        const response = await fetch(`${Theme.routes.cart_change_url}`, { ...fetchConfig('json', { body }), signal });
        const parsedResponse = JSON.parse(await response.text());
        if (!response.ok && !parsedResponse.errors) {
          throw new Error(parsedResponse.description || parsedResponse.message || 'Failed to update cart');
        }
        return parsedResponse;
      });
    }, { sectionIds: Array.from(sectionsToUpdate) })
      .then((parsedResponseText) => {

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText, lineId);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        const newSectionHTML = sectionHtml ? new DOMParser().parseFromString(sectionHtml, 'text/html') : null;

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML?.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : parsedResponseText.item_count;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        deferredUpdatePromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText),
          detail: {
            sections: parsedResponseText.sections,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        // Rendering failure is not a failed mutation: never encourage a second physical add.
        try {
          const rendering = sectionHtml
            ? morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' })
            : sectionRenderer.renderSection(this.sectionId, {
              cache: false, mode: this.isDrawer ? 'hydration' : 'full',
            });
          Promise.resolve(rendering)
            .then(() => {
              this.#updateCartQuantitySelectorButtonStates();
              this.#applyPendingState();
            })
            .catch((error) => console.warn('[cart-items] Section refresh failed:', error));
        } catch (error) {
          console.warn('[cart-items] Section refresh failed:', error);
        }
      })
      .catch(async (error) => {
        console.error(error);
        try {
          const cart = await fetchCartState();
          deferredUpdatePromise.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: {
              items: cart.items, itemCount: cart.item_count,
              source: 'cart-items-component', didError: true,
            },
          });
          // Own events are ignored by #handleCartUpdate, so refresh this component explicitly.
          Promise.resolve(sectionRenderer.renderSection(this.sectionId, {
            cache: false, mode: this.isDrawer ? 'hydration' : 'full',
          })).catch((refreshError) => console.warn('[cart-items] Error recovery refresh failed:', refreshError));
        } catch (refreshError) {
          deferredUpdatePromise.reject(refreshError);
        }

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => {
        resetShimmer(this);
        this.#pendingLines.delete(lineId);
        this.#applyPendingState();
        this.#updateCartQuantitySelectorButtonStates();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText, key) => {
    const itemRow = [...this.querySelectorAll('tr[data-key]')].find((row) => row.dataset.key === key);
    if (!itemRow) return;
    const quantityInput = itemRow?.querySelector('cart-quantity-selector-component input, quantity-selector-component input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = itemRow.querySelector('.cart-item__error-text');
    const cartItemErrorContainer = itemRow.querySelector('.cart-items__error');

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
        /** @type {'hydration' | 'full'} */
        const mode = this.isDrawer ? 'hydration' : 'full';
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

          if (wasEmptyCartDrawer) {
            startViewTransition(() => {
              morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }, ['fill-cart-drawer']);
          } else {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          }

          // Animate newly added rows (reverse of the remove animation).
          if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
            for (const row of this.refs.cartItemRows ?? []) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add('adding');
                onAnimationEnd(row, () => row.classList.remove('adding'));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
          this.#applyPendingState();
        } else {
          sectionRenderer.renderSection(this.sectionId, { cache: false, ...morphOptions });
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
    this.#applyPendingState();
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

// Window capture precedes the cart form's checkout/reward guards.
window.addEventListener('submit', (event) => {
  if (!(event.target instanceof HTMLFormElement) || !event.target.matches('form.cart-form') || event.submitter?.name !== 'checkout') return;
  document.querySelectorAll('cart-items-component').forEach((component) => component.flushQuantityChanges?.());
}, true);
